import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../services/ApiService';
import { useAuth } from '../contexts/AuthContext';
import VehicleCard from './VehicleCard';
import { EmptyState } from './ui/EmptyState';
import { DiscoverSkeleton } from './ui/Skeletons';
import { Button } from './ui/Button';
import { PageHeader } from './ui/PageHeader';
import PageShell from './ui/PageShell';
import { DirectoryBrowseLayout } from './ui/DirectoryBrowseLayout';
import { DiscoverFiltersPanel, BUILD_PURPOSES } from './discover/DiscoverFiltersPanel';
import type { DiscoveryFilters, PaginationMeta } from '../types/discoveryBadges';
import {
  countActiveDiscoveryFilters,
  filtersFromParams,
  filtersToSearchParams,
} from '../utils/discoveryUrlState';
import { vehicleProfilePath } from '../utils/vehicleUrls';
import { insertSponsoredCards } from '../utils/sponsoredInsertion';
import SponsoredCard from './home/SponsoredCard';
import type { AdCreative } from './home/homeTypes';

const SORT_OPTIONS = [
  { value: 'updated_at:desc', label: 'Recently updated' },
  { value: 'created_at:desc', label: 'Recently added' },
  { value: 'year:desc', label: 'Year' },
  { value: 'make:asc', label: 'Make / model' },
  { value: 'documentation_score:desc', label: 'Most documented' },
  { value: 'modification_count:desc', label: 'Most modifications' },
  { value: 'history_entries:desc', label: 'Most history entries' },
  { value: 'award_count:desc', label: 'Most awarded' },
  { value: 'event_participation_count:desc', label: 'Most event participation' },
  { value: 'verified_power:desc', label: 'Highest verified power' },
  { value: 'power_to_weight:desc', label: 'Best power to weight' },
  { value: 'horsepower:desc', label: 'Highest listed horsepower' },
  { value: 'recently_achieved:desc', label: 'Recently achieved' },
  { value: 'recently_dynoed:desc', label: 'Recently dynoed' },
  { value: 'recent_personal_best:desc', label: 'Recent personal best' },
  { value: 'favorite_count:desc', label: 'Most favorited' },
  { value: 'view_count:desc', label: 'Most viewed' },
];

function applyFiltersToParams(
  filters: DiscoveryFilters,
  setParams: (nextInit: URLSearchParams, navigateOpts?: { replace?: boolean }) => void
) {
  setParams(filtersToSearchParams(filters), { replace: true });
}

const DiscoverPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const filters = useMemo(() => filtersFromParams(params), [params]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    limit: 20,
    total_count: 0,
    total_pages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [draft, setDraft] = useState<DiscoveryFilters>(filters);
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [sponsoredCreatives, setSponsoredCreatives] = useState<AdCreative[]>([]);

  useEffect(() => {
    setDraft(filters);
    setSearchInput(filters.search || '');
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [result, ad] = await Promise.all([
        apiService.discoverVehicles(filters),
        apiService.getAdPlacement('discover_native_card').catch(() => null),
      ]);
      setVehicles(result.data);
      setPagination(result.pagination);
      setHasLoadedOnce(true);
      setSponsoredCreatives(ad ? [ad] : []);
    } catch (e: any) {
      setError(e.message || 'Failed to load builds');
      setVehicles([]);
      setSponsoredCreatives([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    void load();
  }, [load]);

  const gridItems = useMemo(
    () =>
      insertSponsoredCards(vehicles, sponsoredCreatives, {
        after: 6,
        max: 2,
      }),
    [vehicles, sponsoredCreatives]
  );

  const activeChips = useMemo(() => {
    const chips: { key: string; label: string }[] = [];
    const add = (key: keyof DiscoveryFilters, label: string) => {
      const v = filters[key];
      if (v !== undefined && v !== null && v !== '' && v !== false) {
        chips.push({ key: String(key), label: `${label}: ${v}` });
      }
    };
    add('make', 'Make');
    add('model', 'Model');
    add('trim', 'Trim');
    add('color', 'Color');
    add('chassis', 'Chassis');
    add('engine', 'Engine');
    add('transmission', 'Transmission');
    add('drivetrain', 'Drivetrain');
    add('build_status', 'Status');
    add('exterior_finish_type', 'Finish');
    add('exterior_finish_color', 'Finish color');
    add('exterior_finish_brand', 'Finish brand');
    add('mod_brand', 'Brand');
    add('mod_category', 'Category');
    add('event_slug', 'Event');
    add('award_title', 'Award');
    add('badge_category', 'Badge');
    add('club_slug', 'Club');
    if (filters.year_min || filters.year_max) {
      chips.push({
        key: 'year',
        label: `Year: ${filters.year_min || '…'}-${filters.year_max || '…'}`,
      });
    }
    if (filters.has_images === 'true' || filters.has_images === true) {
      chips.push({ key: 'has_images', label: 'Has photos' });
    }
    if (filters.has_modifications === 'true' || filters.has_modifications === true) {
      chips.push({ key: 'has_modifications', label: 'Has mods' });
    }
    if (filters.has_awards === 'true' || filters.has_awards === true) {
      chips.push({ key: 'has_awards', label: 'Has awards' });
    }
    if (filters.verified_club === 'true' || filters.verified_club === true) {
      chips.push({ key: 'verified_club', label: 'Verified club' });
    }
    if (filters.certified === 'true' || filters.certified === true) {
      chips.push({ key: 'certified', label: 'Certified builds' });
    }
    if (filters.spec_key && filters.spec_value) {
      chips.push({ key: 'spec', label: `${filters.spec_key}: ${filters.spec_value}` });
    }
    if (filters.build_purpose) {
      const labels = String(filters.build_purpose)
        .split(',')
        .map((p) => {
          const hit = BUILD_PURPOSES.find(([v]) => v === p.trim());
          return hit ? hit[1] : p.trim();
        })
        .filter(Boolean);
      chips.push({ key: 'build_purpose', label: `Purpose: ${labels.join(', ')}` });
    }
    if (filters.doc_score_min) {
      chips.push({ key: 'doc_score_min', label: `Documented ${filters.doc_score_min}+` });
    }
    if (filters.has_verified_dyno === 'true' || filters.has_verified_dyno === true) {
      chips.push({ key: 'has_verified_dyno', label: 'Verified dyno' });
    }
    if (filters.has_performance_record === 'true' || filters.has_performance_record === true) {
      chips.push({ key: 'has_performance_record', label: 'Performance records' });
    }
    if (filters.has_achievement === 'true' || filters.has_achievement === true) {
      chips.push({ key: 'has_achievement', label: 'Has achievements' });
    }
    if (filters.has_shop_contributor === 'true' || filters.has_shop_contributor === true) {
      chips.push({ key: 'has_shop_contributor', label: 'Shop built' });
    }
    if (filters.has_albums === 'true' || filters.has_albums === true) {
      chips.push({ key: 'has_albums', label: 'Has albums' });
    }
    if (filters.power_min || filters.power_max) {
      chips.push({
        key: 'power',
        label: `Power: ${filters.power_min || '0'}-${filters.power_max || 'any'} whp`,
      });
    }
    if (filters.ptw_min) chips.push({ key: 'ptw_min', label: `${filters.ptw_min}+ hp/ton` });
    if (filters.achievement) {
      chips.push({ key: 'achievement', label: `Achievement: ${filters.achievement}` });
    }
    return chips;
  }, [filters]);

  const clearFilter = (key: string) => {
    const next = { ...filters, page: 1 };
    if (key === 'year') {
      next.year_min = undefined;
      next.year_max = undefined;
    } else if (key === 'power') {
      next.power_min = undefined;
      next.power_max = undefined;
    } else if (key === 'spec') {
      next.spec_key = undefined;
      next.spec_value = undefined;
    } else {
      (next as any)[key] = undefined;
    }
    applyFiltersToParams(next, setParams);
  };

  const clearAll = () => {
    applyFiltersToParams({ sort_by: 'updated_at', sort_order: 'desc', page: 1 }, setParams);
    setSearchInput('');
    setDraft({ sort_by: 'updated_at', sort_order: 'desc', page: 1 });
  };

  const submitSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    applyFiltersToParams(
      { ...filters, search: searchInput.trim() || undefined, page: 1 },
      setParams
    );
  };

  const applyDraft = () => {
    applyFiltersToParams(
      { ...draft, search: searchInput.trim() || draft.search, page: 1 },
      setParams
    );
    setMobileFiltersOpen(false);
  };

  const sortValue = `${filters.sort_by || 'updated_at'}:${filters.sort_order || 'desc'}`;
  const hasActiveFilters = activeChips.length > 0 || Boolean(filters.search?.trim());
  const activeFilterCount = countActiveDiscoveryFilters(filters);

  return (
    <PageShell width="full" contentClassName="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 flex-1 w-full">
      <PageHeader
        eyebrow="Browse"
        title="Discover"
        description="Find builds by chassis, engine, drivetrain, events, awards, and clubs."
        backLink={isAuthenticated ? { label: 'Back to Garage', to: '/garage' } : undefined}
      />

      <DirectoryBrowseLayout
        filterTitle="Filters"
        activeFilterCount={activeFilterCount}
        mobileOpen={mobileFiltersOpen}
        onMobileOpenChange={setMobileFiltersOpen}
        sidebar={<DiscoverFiltersPanel draft={draft} onChange={setDraft} />}
        sidebarFooter={
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              className="flex-1"
              onClick={() =>
                setDraft({
                  sort_by: filters.sort_by || 'updated_at',
                  sort_order: filters.sort_order || 'desc',
                })
              }
            >
              Reset
            </Button>
            <Button type="button" variant="primary" className="flex-[2]" onClick={applyDraft}>
              Apply
            </Button>
          </div>
        }
      >
        <form onSubmit={submitSearch} className="flex flex-col sm:flex-row gap-3 mb-4">
          <label className="sr-only" htmlFor="discover-search">
            Search builds
          </label>
          <input
            id="discover-search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search engines, chassis, make…"
            className="flex-1 input min-h-[44px]"
          />
          <Button type="submit" variant="primary">
            Search
          </Button>
        </form>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <div className="flex-1 flex gap-2 overflow-x-auto pb-1" aria-label="Active filters">
            {isAuthenticated ? (
              <button
                type="button"
                onClick={() => navigate('/favorites')}
                className="filter-chip whitespace-nowrap min-h-[36px]"
              >
                My favorites
              </button>
            ) : null}
            {activeChips.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => clearFilter(c.key)}
                className="filter-chip whitespace-nowrap min-h-[36px]"
              >
                {c.label} ×
              </button>
            ))}
            {activeChips.length > 0 ? (
              <button
                type="button"
                className="text-sm text-orange-400 hover:text-orange-300 whitespace-nowrap cursor-pointer"
                onClick={clearAll}
              >
                Clear all
              </button>
            ) : null}
          </div>
          <div className="flex items-center gap-3 text-sm text-gray-400 shrink-0">
            <span aria-live="polite">{pagination.total_count} builds</span>
            <label className="flex items-center gap-2">
              <span className="sr-only">Sort</span>
              <select
                className="select-surface min-h-[44px]"
                value={sortValue}
                onChange={(e) => {
                  const [sort_by, sort_order] = e.target.value.split(':');
                  const advancedSorts = [
                    'documentation_score',
                    'verified_power',
                    'power_to_weight',
                    'history_entries',
                    'recently_achieved',
                    'recently_dynoed',
                    'recent_personal_best',
                  ];
                  if (advancedSorts.includes(sort_by)) {
                    apiService.trackEvent('discover_advanced_sort_applied', {
                      metadata: { sort_by },
                    });
                  }
                  applyFiltersToParams(
                    {
                      ...filters,
                      sort_by,
                      sort_order: sort_order as 'asc' | 'desc',
                      page: 1,
                    },
                    setParams
                  );
                }}
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {loading ? <DiscoverSkeleton fullPage={!hasLoadedOnce} cardCount={6} /> : null}
        {error ? (
          <div
            role="alert"
            className="rounded-lg border border-red-900/50 bg-red-950/40 p-4 text-red-200 mb-4"
          >
            {error}
            <Button type="button" variant="secondary" className="ml-3" onClick={() => void load()}>
              Retry
            </Button>
          </div>
        ) : null}

        {!loading && !error && vehicles.length === 0 ? (
          <EmptyState
            title={hasActiveFilters ? 'No builds match' : 'No public builds yet'}
            description={
              hasActiveFilters
                ? 'Try clearing a filter or searching a broader engine or chassis code.'
                : 'Published builds from the community will appear here as builders share their garages.'
            }
            action={
              hasActiveFilters ? (
                <Button type="button" variant="secondary" onClick={clearAll}>
                  Clear filters
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => navigate(isAuthenticated ? '/garage' : '/register')}
                >
                  {isAuthenticated ? 'Open your garage' : 'Create a garage'}
                </Button>
              )
            }
          />
        ) : null}

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {gridItems.map((entry, index) => {
            if (entry.kind === 'sponsored') {
              return (
                <SponsoredCard
                  key={`sponsored-${entry.item.creative_id}-${index}`}
                  creative={entry.item}
                />
              );
            }
            const v = entry.item;
            return (
              <VehicleCard
                key={v.id}
                vehicle={{
                  ...v,
                  trim: v.trim || '',
                  modification_count: v.modification_count || 0,
                  display_name: v.display_name || `${v.year} ${v.make} ${v.model}`,
                  created_at: v.created_at || '',
                }}
                collectionMode
                showOwner
                onVehicleClick={(vehicle) => {
                  const path = vehicleProfilePath(
                    vehicle.owner_username,
                    vehicle.public_slug || String(vehicle.id)
                  );
                  navigate(path);
                }}
                onUserClick={(username) => navigate(`/${username}`)}
                discoveryBadges={v.badges}
                discoveryBadgeOverflow={v.badge_overflow}
              />
            );
          })}
        </div>

        {pagination.total_pages > 1 ? (
          <div className="flex justify-center gap-3 mt-8">
            <Button
              type="button"
              variant="secondary"
              disabled={pagination.page <= 1}
              onClick={() =>
                applyFiltersToParams({ ...filters, page: pagination.page - 1 }, setParams)
              }
            >
              Previous
            </Button>
            <span className="text-gray-400 self-center text-sm">
              Page {pagination.page} of {pagination.total_pages}
            </span>
            <Button
              type="button"
              variant="secondary"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() =>
                applyFiltersToParams({ ...filters, page: pagination.page + 1 }, setParams)
              }
            >
              Next
            </Button>
          </div>
        ) : null}
      </DirectoryBrowseLayout>
    </PageShell>
  );
};

export default DiscoverPage;
